import React, { useEffect, useRef } from 'react';
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers } from 'lightweight-charts';

function StockChartCard({ holding }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartContainerRef.current || !holding?.candles?.length) return;

    // 차트 생성
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 200,
      layout: {
        background: { type: 'solid', color: '#ffffff' },
        textColor: '#6b7280',
        fontSize: 10,
      },
      grid: {
        vertLines: { color: '#f3f4f6' },
        horzLines: { color: '#f3f4f6' },
      },
      crosshair: {
        mode: 0,  // 자유 이동 모드
      },
      rightPriceScale: {
        borderColor: '#e5e7eb',
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      timeScale: {
        borderColor: '#e5e7eb',
        timeVisible: false,
        secondsVisible: false,
      },
      handleScroll: true,
      handleScale: true,
    });

    chartRef.current = chart;

    // 캔들스틱 시리즈 (현재가 라인 숨김)
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#ef4444',
      downColor: '#3b82f6',
      borderUpColor: '#ef4444',
      borderDownColor: '#3b82f6',
      wickUpColor: '#ef4444',
      wickDownColor: '#3b82f6',
      priceLineVisible: false,
      lastValueVisible: false,
    });

    // 캔들 데이터 형식 변환
    const candleData = holding.candles.map(c => ({
      time: c.time,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));
    candlestickSeries.setData(candleData);

    // 매수가 가로 점선 추가
    if (holding.entry_price) {
      candlestickSeries.createPriceLine({
        price: holding.entry_price,
        color: '#9ca3af',
        lineWidth: 1,
        lineStyle: 2, // Dashed
        axisLabelVisible: true,
        title: '',
      });
    }

    // MA20 라인 시리즈
    const ma20Data = holding.candles
      .filter(c => c.ma20 != null)
      .map(c => ({
        time: c.time,
        value: c.ma20,
      }));

    if (ma20Data.length > 0) {
      const ma20Series = chart.addSeries(LineSeries, {
        color: '#f59e0b',
        lineWidth: 1,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      ma20Series.setData(ma20Data);
    }

    // 매수 마커 추가 (짙은 회색)
    if (holding.entry_date && holding.entry_price) {
      createSeriesMarkers(candlestickSeries, [
        {
          time: holding.entry_date,
          position: 'belowBar',
          color: '#4b5563',
          shape: 'arrowUp',
          text: 'B',
        }
      ]);
    }

    // 차트 크기 조정 - 최근 60일만 보이도록 설정
    const dataLength = candleData.length;
    if (dataLength > 60) {
      chart.timeScale().setVisibleLogicalRange({
        from: dataLength - 60,
        to: dataLength,
      });
    } else {
      chart.timeScale().fitContent();
    }

    // 리사이즈 핸들러
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [holding]);

  if (!holding?.candles?.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex justify-between items-center mb-2">
          <div>
            <span className="font-medium text-gray-800 text-sm">{holding?.stock_name || '-'}</span>
            <span className="text-xs text-gray-500 ml-2">{holding?.stock_code || ''}</span>
          </div>
        </div>
        <div className="h-[200px] flex items-center justify-center text-gray-400 text-xs">
          차트 데이터가 없습니다
        </div>
      </div>
    );
  }

  // 수익률 및 보유일 계산
  const lastCandle = holding.candles[holding.candles.length - 1];
  const currentPrice = lastCandle?.close || 0;
  const profitRate = holding.entry_price > 0
    ? ((currentPrice - holding.entry_price) / holding.entry_price * 100)
    : 0;

  // 보유일 계산
  let holdingDays = '-';
  if (holding.entry_date) {
    const entryDate = new Date(holding.entry_date);
    const today = new Date();
    const diffTime = today - entryDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    holdingDays = diffDays === 0 ? '진입' : `${diffDays}일`;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-3">
      <div className="flex justify-between items-center mb-2">
        <div>
          <span className="font-medium text-gray-800 text-sm">{holding.stock_name}</span>
          <span className="text-xs text-gray-500 ml-2">{holding.stock_code}</span>
        </div>
        <div className="text-right flex items-center gap-2">
          <span className={`text-sm font-medium tabular-nums ${profitRate >= 0 ? 'text-red-500' : 'text-blue-500'}`}>
            {profitRate >= 0 ? '+' : ''}{profitRate.toFixed(2)}%
          </span>
          <span className="text-xs text-gray-500 tabular-nums">
            {holdingDays}
          </span>
        </div>
      </div>
      <div ref={chartContainerRef} />
    </div>
  );
}

export default StockChartCard;
